import { FC, useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Save,
  Cancel,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledAutocomplete from "../../components/controlledComponents/ControlledAutocomplete";
import {
  TEvaluationFramework,
  TEvaluationModel,
  TEvaluationFrequency,
  TEvaluationFrameworkComponent,
} from "../../../apis/assessment/assessment.interfaces";
import {
  useCreateEvaluationFrameworkHook,
  useUpdateEvaluationFrameworkHook,
} from "../../../hooks/useAssessmentHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import {
  useClassHook,
  useGradeLevelHook,
} from "../../../hooks/useAcademicHook";

const componentSchema = z.object({
  name: z
    .string()
    .min(1, "Component name is required")
    .max(100, "Component name must not exceed 100 characters"),
  description: z
    .string()
    .max(500, "Description must not exceed 500 characters")
    .optional()
    .or(z.literal("")),
  weightPercentage: z
    .number()
    .min(0, "Weight must be at least 0%")
    .max(100, "Weight must not exceed 100%")
    .optional(),
});

const evaluationFrameworkSchema = z.object({
  name: z
    .string()
    .min(1, "Framework name is required")
    .min(2, "Framework name must be at least 2 characters")
    .max(200, "Framework name must not exceed 200 characters"),
  classIds: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
      }),
    )
    .min(1, "At least one class must be selected"),
  evaluationModel: z.enum(
    ["semester-based", "annual", "continuous-evaluation"],
    {
      errorMap: () => ({ message: "Please select a valid evaluation model" }),
    },
  ),
  evaluationFrequency: z.enum(
    ["weekly", "bi-weekly", "monthly", "quarterly", "semesterly", "annually"],
    {
      errorMap: () => ({
        message: "Please select a valid evaluation frequency",
      }),
    },
  ),
  assessmentComponents: z
    .array(componentSchema)
    .min(1, "At least one assessment component is required"),
  gradingMethodology: z
    .string()
    .max(1000, "Grading methodology must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
  gradeLevelIds: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
      }),
    )
    .min(1, "At least one grade level must be selected"),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
});

type EvaluationFrameworkFormData = z.infer<typeof evaluationFrameworkSchema>;

interface ManageEvaluationFrameworkDialogProps {
  open: boolean;
  onClose: () => void;
  framework?: TEvaluationFramework | null;
}

const ManageEvaluationFrameworkDialog: FC<
  ManageEvaluationFrameworkDialogProps
> = ({ open, onClose, framework }) => {
  const { selectedInstitute } = useInstituteContext();
  const createFrameworkMutation = useCreateEvaluationFrameworkHook();
  const updateFrameworkMutation = useUpdateEvaluationFrameworkHook();
  const { data: classes } = useClassHook();
  const { data: gradeLevels } = useGradeLevelHook();
  const isEditMode = !!framework;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EvaluationFrameworkFormData>({
    resolver: zodResolver(evaluationFrameworkSchema),
    defaultValues: {
      name: "",
      classIds: [],
      evaluationModel: "semester-based",
      evaluationFrequency: "semesterly",
      assessmentComponents: [
        { name: "", description: "", weightPercentage: 0 },
      ],
      gradingMethodology: "",
      gradeLevelIds: [],
      description: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "assessmentComponents",
  });

  const watchedComponents = watch("assessmentComponents");
  const totalWeight = useMemo(() => {
    return watchedComponents.reduce(
      (sum, comp) => sum + (comp.weightPercentage || 0),
      0,
    );
  }, [watchedComponents]);

  const classOptions = useMemo(() => {
    return (
      classes?.map((cls) => ({
        id: cls.id,
        label: cls.name,
      })) || []
    );
  }, [classes]);

  const gradeLevelOptions = useMemo(() => {
    return (
      gradeLevels?.map((level) => ({
        id: level.id,
        label: level.name,
      })) || []
    );
  }, [gradeLevels]);

  useEffect(() => {
    if (framework && classes && gradeLevels) {
      const selectedClasses = framework.classIds
        .map((classId) => {
          const cls = classes.find((c) => c.id === classId);
          return cls ? { id: cls.id, label: cls.name } : null;
        })
        .filter((c): c is { id: string; label: string } => c !== null);

      const selectedGradeLevels = framework.gradeLevelIds
        .map((levelId) => {
          const level = gradeLevels.find((l) => l.id === levelId);
          return level ? { id: level.id, label: level.name } : null;
        })
        .filter((l): l is { id: string; label: string } => l !== null);

      reset({
        name: framework.name,
        classIds: selectedClasses,
        evaluationModel: framework.evaluationModel,
        evaluationFrequency: framework.evaluationFrequency,
        assessmentComponents: framework.assessmentComponents.map((comp) => ({
          name: comp.name,
          description: comp.description || "",
          weightPercentage: comp.weightPercentage,
        })),
        gradingMethodology: framework.gradingMethodology || "",
        gradeLevelIds: selectedGradeLevels,
        description: framework.description || "",
      });
    } else {
      reset({
        name: "",
        classIds: [],
        evaluationModel: "semester-based",
        evaluationFrequency: "semesterly",
        assessmentComponents: [
          { name: "", description: "", weightPercentage: 0 },
        ],
        gradingMethodology: "",
        gradeLevelIds: [],
        description: "",
      });
    }
  }, [framework, reset, open, classes, gradeLevels]);

  const onSubmit = async (data: EvaluationFrameworkFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    try {
      const components: Omit<TEvaluationFrameworkComponent, "id">[] =
        data.assessmentComponents.map((comp) => ({
          name: comp.name,
          description: comp.description || undefined,
          weightPercentage: comp.weightPercentage,
        }));

      if (isEditMode && framework) {
        await updateFrameworkMutation.mutateAsync({
          id: framework.id,
          request: {
            name: data.name,
            classIds: data.classIds.map((c) => c.id),
            evaluationModel: data.evaluationModel,
            evaluationFrequency: data.evaluationFrequency,
            assessmentComponents: components,
            gradingMethodology: data.gradingMethodology || undefined,
            gradeLevelIds: data.gradeLevelIds.map((l) => l.id),
            description: data.description || undefined,
          },
        });
        logger.show_success("Evaluation framework updated successfully!");
      } else {
        await createFrameworkMutation.mutateAsync({
          name: data.name,
          classIds: data.classIds.map((c) => c.id),
          evaluationModel: data.evaluationModel,
          evaluationFrequency: data.evaluationFrequency,
          assessmentComponents: components,
          gradingMethodology: data.gradingMethodology || undefined,
          gradeLevelIds: data.gradeLevelIds.map((l) => l.id),
          description: data.description || undefined,
          instituteId: selectedInstitute.id,
        });
        logger.show_success("Evaluation framework created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} evaluation framework. Please try again.`,
      );
    }
  };

  const handleAddComponent = () => {
    append({ name: "", description: "", weightPercentage: 0 });
  };

  const handleRemoveComponent = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const isLoading =
    isSubmitting ||
    createFrameworkMutation.isPending ||
    updateFrameworkMutation.isPending;
  const isError =
    createFrameworkMutation.isError || updateFrameworkMutation.isError;
  const errorMessage =
    createFrameworkMutation.error?.message ||
    updateFrameworkMutation.error?.message;

  const evaluationModelOptions: { key: TEvaluationModel; value: string }[] = [
    { key: "semester-based", value: "Semester-Based" },
    { key: "annual", value: "Annual" },
    { key: "continuous-evaluation", value: "Continuous Evaluation" },
  ];

  const evaluationFrequencyOptions: {
    key: TEvaluationFrequency;
    value: string;
  }[] = [
    { key: "weekly", value: "Weekly" },
    { key: "bi-weekly", value: "Bi-Weekly" },
    { key: "monthly", value: "Monthly" },
    { key: "quarterly", value: "Quarterly" },
    { key: "semesterly", value: "Semesterly" },
    { key: "annually", value: "Annually" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-evaluation-framework-dialog"
    >
      <DialogTitle data-testid="manage-evaluation-framework-dialog-title">
        {isEditMode
          ? "Edit Evaluation Framework"
          : "Add New Evaluation Framework"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="evaluation-framework-form"
      >
        <DialogContent data-testid="evaluation-framework-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="evaluation-framework-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} evaluation framework. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="evaluation-framework-form-container"
          >
            <ControlledTextField
              control={control}
              name="name"
              id="evaluation-framework-name"
              label="Framework Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "evaluation-framework-name-input" }}
            />

            <Grid item xs={12}>
              <ControlledAutocomplete
                control={control}
                name="classIds"
                id="evaluation-framework-classes"
                label="Classes"
                dataOptions={classOptions}
                isMultiSelect
                errorMessage={errors.classIds?.message}
                dataTestId="evaluation-framework-classes-input"
              />
            </Grid>

            <ControlledSelectField
              control={control}
              name="evaluationModel"
              id="evaluation-framework-model"
              label="Evaluation Model"
              itemList={evaluationModelOptions}
              isRequired
              errorMessage={errors.evaluationModel?.message}
            />

            <ControlledSelectField
              control={control}
              name="evaluationFrequency"
              id="evaluation-framework-frequency"
              label="Evaluation Frequency"
              itemList={evaluationFrequencyOptions}
              isRequired
              errorMessage={errors.evaluationFrequency?.message}
            />

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Assessment Components</Typography>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddComponent}
                  data-testid="add-assessment-component-button"
                >
                  Add Component
                </Button>
              </Box>
              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle2">
                      Component {index + 1}
                    </Typography>
                    {fields.length > 1 && (
                      <IconButton
                        type="button"
                        size="small"
                        onClick={() => handleRemoveComponent(index)}
                        color="error"
                        data-testid={`remove-component-${index}-button`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control}
                        name={`assessmentComponents.${index}.name`}
                        id={`component-${index}-name`}
                        label="Component Name"
                        required
                        errorMessage={
                          errors.assessmentComponents?.[index]?.name?.message
                        }
                        inputProps={{
                          "data-testid": `component-${index}-name-input`,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control}
                        name={`assessmentComponents.${index}.description`}
                        id={`component-${index}-description`}
                        label="Description"
                        multiline
                        rows={2}
                        errorMessage={
                          errors.assessmentComponents?.[index]?.description
                            ?.message
                        }
                        inputProps={{
                          "data-testid": `component-${index}-description-input`,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control}
                        name={`assessmentComponents.${index}.weightPercentage`}
                        id={`component-${index}-weight`}
                        label="Weight Percentage"
                        type="number"
                        errorMessage={
                          errors.assessmentComponents?.[index]?.weightPercentage
                            ?.message
                        }
                        inputProps={{
                          "data-testid": `component-${index}-weight-input`,
                          min: 0,
                          max: 100,
                          step: 0.1,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
              {totalWeight > 0 && (
                <Typography
                  variant="body2"
                  color={
                    Math.abs(totalWeight - 100) < 0.01
                      ? "success.main"
                      : "error.main"
                  }
                  sx={{ mt: 1 }}
                >
                  Total Weight: {totalWeight.toFixed(1)}%
                </Typography>
              )}
            </Grid>

            <ControlledTextField
              control={control}
              name="gradingMethodology"
              id="evaluation-framework-grading-methodology"
              label="Grading Methodology"
              multiline
              rows={3}
              errorMessage={errors.gradingMethodology?.message}
              inputProps={{
                "data-testid": "evaluation-framework-grading-methodology-input",
              }}
            />

            <Grid item xs={12}>
              <ControlledAutocomplete
                control={control}
                name="gradeLevelIds"
                id="evaluation-framework-grade-levels"
                label="Grade Levels"
                dataOptions={gradeLevelOptions}
                isMultiSelect
                errorMessage={errors.gradeLevelIds?.message}
                dataTestId="evaluation-framework-grade-levels-input"
              />
            </Grid>

            <ControlledTextField
              control={control}
              name="description"
              id="evaluation-framework-description"
              label="Description"
              multiline
              rows={3}
              errorMessage={errors.description?.message}
              inputProps={{
                "data-testid": "evaluation-framework-description-input",
              }}
            />
          </Grid>
        </DialogContent>
        <DialogActions data-testid="evaluation-framework-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-evaluation-framework-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-evaluation-framework-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Framework"
                : "Create Framework"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageEvaluationFrameworkDialog;
